import {
  Field,
  type FieldRootProps,
  Portal,
  type SelectRootProps,
  Select as UnstyledSelect,
  createListCollection,
} from "@ark-ui/react";
import { CaretDown, X } from "@phosphor-icons/react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import clsx from "clsx";
import * as React from "react";
import getFormHelperText, {
  type HelperTextProps,
} from "../../helper/HelperTextIcon.js";
import {
  FormLabel,
  type FormLabelClassNames,
  type LabelOptionProps,
} from "../../shared/form-label/FormLabel.js";
import { Checkbox } from "../checkbox/Checkbox.js";
import { Flex } from "../flex/Flex.js";
import { Search } from "../search/Search.js";
import { useThemeContext } from "../theme/Theme.js";

export interface SelectItemObject {
  value: string | number;
  label: string;
  disabled?: boolean;
}
export type SelectDataProps = string[] | SelectItemObject[];

interface SelectProps extends Omit<SelectRootProps<any>, "collection"> {
  size?: "xs" | "sm" | "md" | "lg";
  label?: LabelOptionProps;
  valuePlaceholder?: string;
  valueFormatter?: (
    value: string[] | number[],
    valueAsString: string,
  ) => React.ReactNode;
  items: SelectDataProps;
  helperText?: HelperTextProps;
  wrapItemText?: boolean;
  error?: boolean;
  variant?: "outline" | "underline";
  disablePortal?: boolean;
  fieldProps?: FieldRootProps;
  labelClassNames?: FormLabelClassNames;
  classNames?: {
    field?: string;
    trigger?: string;
    value?: string;
    content?: string;
    item?: string;
    root?: string;
    label?: string;
    control?: string;
    itemText?: string;
    helperText?: string;
    searchField?: string;
    searchInput?: string;
    noResults?: string;
  };
  withCheckbox?: boolean;
  withClear?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  noResultsText?: string;
}
const Select = React.forwardRef<
  React.ElementRef<typeof UnstyledSelect.HiddenSelect>,
  SelectProps
>((props, ref) => {
  const {
    valuePlaceholder = "Select",
    size = "md",
    label,
    classNames,
    variant = "outline",
    items,
    helperText = { message: "", status: "default" },
    fieldProps,
    error,
    disablePortal = false,
    multiple = false,
    wrapItemText = false,
    withCheckbox = multiple,
    labelClassNames,
    valueFormatter,
    closeOnSelect,
    withClear = false,
    searchable = false,
    searchPlaceholder = "Search...",
    noResultsText = "No items found",
    ...rest
  } = props;

  const { primaryColor, secondaryColor } = useThemeContext();
  const [searchValue, setSearchValue] = React.useState("");

  const normalizedItems = React.useMemo(() => {
    return items.map((item) => {
      if (typeof item === "string") {
        return {
          label: item,
          value: item,
        } as SelectItemObject;
      } else {
        return {
          label: item.label,
          value: item.value,
          disabled: item.disabled,
        } as SelectItemObject;
      }
    });
  }, [items]);

  const filteredItems = React.useMemo(() => {
    if (!searchValue.trim()) {
      return normalizedItems;
    } else {
      return normalizedItems.filter((item) =>
        item.label.toLowerCase().includes(searchValue.toLowerCase()),
      );
    }
  }, [normalizedItems, searchValue]);

  const internalCollection = React.useMemo(() => {
    return createListCollection({
      items: filteredItems,
    });
  }, [filteredItems]);

  return (
    <Field.Root
      {...fieldProps}
      invalid={error}
      className={clsx(classNames?.field, fieldProps?.className)}
    >
      <UnstyledSelect.Root
        className={clsx("ox-reset ox-SelectRoot", classNames?.root)}
        collection={internalCollection}
        multiple={multiple}
        closeOnSelect={!multiple}
        {...rest}
      >
        {/* label  */}
        {label ? (
          <FormLabel
            label={label}
            size={size}
            error={error}
            classNames={labelClassNames}
            component={UnstyledSelect.Label}
          />
        ) : null}
        {/* Trigger button  */}
        <UnstyledSelect.Control
          data-size={size}
          className={clsx("ox-reset ox-SelectControl", classNames?.control)}
        >
          <UnstyledSelect.Trigger
            data-size={size}
            data-variant={variant}
            className={clsx("ox-reset ox-SelectTrigger", classNames?.trigger)}
          >
            <UnstyledSelect.Context>
              {(context) => (
                <>
                  <UnstyledSelect.ValueText
                    className={clsx(
                      "ox-reset ox-SelectValue",
                      classNames?.value,
                    )}
                    placeholder={valuePlaceholder}
                    data-placeholder={!context.hasSelectedItems}
                  >
                    {!context.hasSelectedItems
                      ? valuePlaceholder
                      : valueFormatter
                        ? valueFormatter(context.value, context.valueAsString)
                        : context.valueAsString}
                  </UnstyledSelect.ValueText>
                  <UnstyledSelect.Indicator
                    className={clsx("ox-reset ox-SelectIndicator")}
                  >
                    {!withClear ? (
                      <CaretDown
                        fill="currentColor"
                        className={clsx("ox-reset ox-SelectIndicatorSVG")}
                        data-size={size}
                      />
                    ) : context.hasSelectedItems ? (
                      <UnstyledSelect.ClearTrigger
                        asChild
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <X
                          className={clsx("ox-reset ox-SelectIndicatorSVG")}
                          data-size={size}
                        />
                      </UnstyledSelect.ClearTrigger>
                    ) : (
                      <CaretDown
                        fill="currentColor"
                        className={clsx("ox-reset ox-SelectIndicatorSVG")}
                        data-size={size}
                      />
                    )}
                  </UnstyledSelect.Indicator>
                </>
              )}
            </UnstyledSelect.Context>
          </UnstyledSelect.Trigger>
        </UnstyledSelect.Control>
        {/* Portal  */}

        <Portal disabled={disablePortal}>
          <UnstyledSelect.Positioner
            data-primary-color={primaryColor}
            data-secondary-color={secondaryColor}
          >
            <ScrollAreaPrimitive.Root
              type="hover"
              className="ox-reset ox-ScrollAreaRoot"
            >
              <UnstyledSelect.Content asChild data-size={size}>
                <ScrollAreaPrimitive.Viewport
                  className={clsx(
                    "ox-reset ox-SelectContent",
                    classNames?.content,
                  )}
                >
                  {searchable && (
                    <Search
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      placeholder={searchPlaceholder}
                      data-size={size}
                      onMouseDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          setSearchValue("");
                        }
                        e.stopPropagation();
                      }}
                    />
                  )}
                  {internalCollection.items.length > 0 ? (
                    internalCollection.items.map((item) => (
                      <UnstyledSelect.Item
                        data-size={size}
                        className={clsx(
                          "ox-reset ox-SelectItem",
                          classNames?.item,
                        )}
                        item={item}
                        key={item.value}
                      >
                        <UnstyledSelect.Context>
                          {(context) => (
                            <>
                              {multiple && withCheckbox ? (
                                <UnstyledSelect.Indicator
                                  tabIndex={-1}
                                  className="ox-SelectItemIndicator"
                                >
                                  <Checkbox
                                    tabIndex={-1}
                                    readOnly
                                    checked={context.value.includes(
                                      item.value as string,
                                    )}
                                  />
                                </UnstyledSelect.Indicator>
                              ) : null}
                              <UnstyledSelect.ItemText
                                data-wrapped={wrapItemText}
                                className={clsx(
                                  "ox-reset ox-SelectItemText",
                                  classNames?.itemText,
                                )}
                              >
                                {item.label}
                              </UnstyledSelect.ItemText>
                            </>
                          )}
                        </UnstyledSelect.Context>
                      </UnstyledSelect.Item>
                    ))
                  ) : (
                    <FormLabel
                      label={noResultsText}
                      size={size}
                      error={true}
                      classNames={labelClassNames}
                      component={UnstyledSelect.Label}
                    />
                  )}
                </ScrollAreaPrimitive.Viewport>
              </UnstyledSelect.Content>
              <ScrollAreaPrimitive.Scrollbar
                orientation="vertical"
                className="ox-reset ox-ScrollAreaScrollbar"
              >
                <ScrollAreaPrimitive.Thumb className="ox-reset ox-ScrollAreaThumb" />
              </ScrollAreaPrimitive.Scrollbar>
            </ScrollAreaPrimitive.Root>
          </UnstyledSelect.Positioner>
        </Portal>

        <UnstyledSelect.HiddenSelect ref={ref} />
      </UnstyledSelect.Root>
      {helperText?.message ? (
        <Field.HelperText
          data-size={size}
          data-status={helperText?.status}
          className={clsx("ox-reset ox-FormHelperText", classNames?.helperText)}
        >
          {getFormHelperText(helperText, size)}
        </Field.HelperText>
      ) : null}
    </Field.Root>
  );
});
Select.displayName = "Select";
export { Select };
export type { SelectProps };
